import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  GridItem,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { FiFileText, FiSearch } from "react-icons/fi";
import { useQuery } from "react-query";
import { getPatientFormsApi } from "../../../../api/request";
import attachmentKeys from "../../../../utils/attachmentKeys";
import FileList from "../../../../components/files/FileList";
import PrescriptionItemsDisplay from "../../../../components/forms/PrescriptionItemsDisplay";

const workerName = (worker) => {
  if (!worker) return "Not available";
  if (typeof worker === "string") return "Worker details unavailable";
  return `${worker.firstName || ""} ${worker.lastName || ""}`.trim() || "Unknown worker";
};

const readableKey = (key = "") =>
  attachmentKeys[key] ||
  key
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (character) => character.toUpperCase());

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not recorded";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (!value.length) return "Not recorded";
    return value.map(formatValue).join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).filter(([, nestedValue]) =>
      nestedValue !== null && nestedValue !== undefined && nestedValue !== "",
    );
    if (!entries.length) return "Not recorded";
    return entries
      .map(([key, nestedValue]) => `${readableKey(key)}: ${formatValue(nestedValue)}`)
      .join(" | ");
  }
  return attachmentKeys[value] || String(value);
};

const formatDate = (value) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return new Intl.DateTimeFormat("en-NG", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const FormHistory = ({
  patientData,
  types,
  title = "Request form history",
  description = "Forms created for this patient, including their latest content and custody status.",
  emptyTitle = "No request forms yet",
  emptyDescription = "Forms created for this patient will appear here.",
}) => {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const patientId = patientData?._id;
  const formsQuery = useQuery(
    ["getPatientFormsApi", patientId],
    () => getPatientFormsApi(patientId),
    { enabled: Boolean(patientId) },
  );
  const forms = useMemo(() => {
    const allForms = formsQuery.data?.data?.requestForms || [];
    if (!types?.length) return allForms;
    return allForms.filter((form) => types.includes(form.type));
  }, [formsQuery.data, types]);

  const filteredForms = useMemo(() => {
    const term = search.trim().toLowerCase();
    return forms.filter((form) => {
      const matchesStatus =
        status === "all" ||
        (status === "archived" ? form.deleted : !form.deleted);
      if (!matchesStatus) return false;
      if (!term) return true;
      const searchable = [
        form.type,
        workerName(form.currentHolder),
        workerName(form.createdBy),
        ...Object.keys(form.form || {}).map(readableKey),
        ...Object.values(form.form || {}).map(formatValue),
      ].join(" ").toLowerCase();
      return searchable.includes(term);
    });
  }, [forms, search, status]);

  const activeCount = forms.filter((form) => !form.deleted).length;
  const archivedCount = forms.length - activeCount;

  if (formsQuery.isLoading) {
    return (
      <Stack spacing="4">
        {[1, 2, 3].map((item) => (
          <Skeleton key={item} height="104px" borderRadius="xl" />
        ))}
      </Stack>
    );
  }

  if (formsQuery.isError) {
    return (
      <Alert status="error" borderRadius="xl" alignItems="flex-start">
        <AlertIcon mt="1px" />
        <Box flex="1">
          <Text fontWeight="700">Unable to load form history</Text>
          <AlertDescription fontSize="sm">
            {formsQuery.error?.response?.data?.msg || "Please check your connection and try again."}
          </AlertDescription>
          <Button mt="3" size="sm" variant="outline" onClick={() => formsQuery.refetch()}>
            Try again
          </Button>
        </Box>
      </Alert>
    );
  }

  return (
    <Box>
      <Flex mb="5" gap="4" justify="space-between" align={{ base: "flex-start", md: "center" }} direction={{ base: "column", md: "row" }}>
        <Box>
          <HStack spacing="3">
            <Heading size="md">{title}</Heading>
            <Badge colorScheme="blue" borderRadius="full" px="2">{forms.length}</Badge>
          </HStack>
          <Text mt="1" color="gray.500" fontSize="sm">
            {description}
          </Text>
        </Box>
        {!!forms.length && (
          <HStack spacing="2" fontSize="sm">
            <Badge colorScheme="green" variant="subtle">{activeCount} active</Badge>
            {!!archivedCount && <Badge colorScheme="gray">{archivedCount} archived</Badge>}
          </HStack>
        )}
      </Flex>

      {!!forms.length && (
        <Flex mb="4" gap="3" direction={{ base: "column", sm: "row" }}>
          <InputGroup flex="1">
            <InputLeftElement pointerEvents="none" color="gray.400"><FiSearch /></InputLeftElement>
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search form type, field or worker"
              bg="white"
            />
          </InputGroup>
          <Select value={status} onChange={(event) => setStatus(event.target.value)} width={{ base: "full", sm: "180px" }} bg="white">
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </Select>
        </Flex>
      )}

      {!forms.length ? (
        <Box borderWidth="1px" borderStyle="dashed" borderColor="gray.300" borderRadius="xl" py="12" px="5" textAlign="center">
          <Flex mx="auto" mb="3" boxSize="12" borderRadius="full" bg="gray.100" color="gray.500" align="center" justify="center">
            <FiFileText size="22" />
          </Flex>
          <Text fontWeight="700">{emptyTitle}</Text>
          <Text mt="1" color="gray.500" fontSize="sm">{emptyDescription}</Text>
        </Box>
      ) : !filteredForms.length ? (
        <Box borderWidth="1px" borderRadius="xl" py="10" px="5" textAlign="center">
          <Text fontWeight="700">No matching forms</Text>
          <Text mt="1" color="gray.500" fontSize="sm">Try another search term or status.</Text>
          <Button mt="4" size="sm" variant="outline" onClick={() => { setSearch(""); setStatus("all"); }}>Clear filters</Button>
        </Box>
      ) : (
        <Accordion allowMultiple defaultIndex={filteredForms.length === 1 ? [0] : []}>
          <Stack spacing="3">
            {filteredForms.map((form) => {
              const fields = Object.entries(form.form || {});
              return (
                <AccordionItem key={form._id} borderWidth="1px" borderRadius="xl" overflow="hidden" bg="white">
                  <AccordionButton px={{ base: "4", md: "5" }} py="4" _hover={{ bg: "gray.50" }}>
                    <Flex flex="1" minW="0" gap="3" align="center" textAlign="left">
                      <Flex flexShrink="0" boxSize="10" borderRadius="lg" bg={form.deleted ? "gray.100" : "blue.50"} color={form.deleted ? "gray.500" : "blue.600"} align="center" justify="center">
                        <FiFileText size="19" />
                      </Flex>
                      <Box minW="0">
                        <HStack spacing="2" flexWrap="wrap">
                          <Text fontWeight="800" textTransform="capitalize" noOfLines={1}>{form.type || "Untitled"} form</Text>
                          <Badge colorScheme={form.deleted ? "gray" : "green"}>{form.deleted ? "Archived" : "Active"}</Badge>
                          {form.version > 1 && <Badge variant="outline">Version {form.version}</Badge>}
                        </HStack>
                        <Text mt="1" color="gray.500" fontSize="xs">
                          Updated {formatDate(form.updatedAt || form.createdAt)}
                        </Text>
                      </Box>
                    </Flex>
                    <AccordionIcon ml="3" />
                  </AccordionButton>
                  <AccordionPanel px={{ base: "4", md: "5" }} pb="5">
                    <Divider mb="4" />
                    <SimpleGrid columns={{ base: 1, md: 3 }} gap="3" mb="5">
                      <Box><Text color="gray.500" fontSize="xs" fontWeight="700">CREATED BY</Text><Text mt="1" fontSize="sm" fontWeight="600">{workerName(form.createdBy)}</Text></Box>
                      <Box><Text color="gray.500" fontSize="xs" fontWeight="700">CURRENT HOLDER</Text><Text mt="1" fontSize="sm" fontWeight="600">{form.deleted ? "Archived" : workerName(form.currentHolder)}</Text></Box>
                      <Box><Text color="gray.500" fontSize="xs" fontWeight="700">CREATED</Text><Text mt="1" fontSize="sm" fontWeight="600">{formatDate(form.createdAt)}</Text></Box>
                    </SimpleGrid>

                    <Heading size="xs" mb="3" color="gray.600">FORM CONTENT</Heading>
                    {!fields.length ? (
                      <Text color="gray.500" fontSize="sm">No form content was recorded.</Text>
                    ) : (
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing="3">
                        {fields.map(([key, value]) => ["prescriptionItems", "injectionItems"].includes(key) ? (
                          <GridItem key={key} colSpan={{ base: 1, md: 2 }} bg="gray.50" borderRadius="lg" p="3">
                            <Text mb="2" color="gray.500" fontSize="xs" fontWeight="700" textTransform="uppercase">{key === "injectionItems" ? "Injections" : "Medications"}</Text>
                            <PrescriptionItemsDisplay items={value} />
                          </GridItem>
                        ) : (
                          <GridItem key={key} colSpan={typeof value === "object" && value !== null ? { base: 1, md: 2 } : 1} bg="gray.50" borderRadius="lg" p="3">
                            <Text color="gray.500" fontSize="xs" fontWeight="700" textTransform="uppercase">{readableKey(key)}</Text>
                            <Text mt="1" color="gray.800" fontSize="sm" whiteSpace="pre-wrap" overflowWrap="anywhere">{formatValue(value)}</Text>
                          </GridItem>
                        ))}
                      </SimpleGrid>
                    )}
                    <FileList files={form.files || []} />
                  </AccordionPanel>
                </AccordionItem>
              );
            })}
          </Stack>
        </Accordion>
      )}
    </Box>
  );
};

export default FormHistory;
